import {Icon} from '@udemy-v2/icons';
import {Button, IconButton} from '@udemy-v2/react-core-components';
import {BottomDrawer, Modal} from '@udemy-v2/react-dialog-components';
import {Loader} from '@udemy-v2/react-reveal-components';
import React, {useCallback, useState} from 'react';
import {useDeviceType} from '@udemy-v2/hooks';
import {useI18n} from '@udemy-v2/i18n';

import {SOURCE_PAGES} from './constants';
import {isMarketplaceShareEnabled} from './helpers';
import {useSocialShare} from './hooks/use-social-share';
import {ShareableObject, ChannelType} from './types';
import {CourseShareEvent} from '../../events/events';
import {publishEvent} from '@udemy-v2/event-tracking';

type ButtonStyle = 'primary' | 'ghost' | 'secondary' | 'knockout-outline' | 'knockout-solid';
type Size = 'small' | 'medium' | 'large';

export interface ShareContentProps {
    isImportEmailModalShown: boolean;
    sharedUrl: string;
    hideImportEmailModal: () => void;
    onFormCancel: () => void;
    share: (channel: ChannelType, onError?: (error: Error) => void) => Promise<void>;
    sourcePage: string;
    shareableObject: ShareableObject;
}

export interface LoadedModule {
    default: React.ComponentType<ShareContentProps>;
}

export interface MarketplaceSocialShareButtonProps {
    /** Button style variant */
    buttonStyle?: ButtonStyle;
    /** Context where the share button is used */
    context: string;
    /** Whether the button should take full width */
    fullWidth?: boolean;
    /** Function to lazily load the share content */
    load: () => Promise<LoadedModule>;
    /** Object to be shared */
    shareableObject: ShareableObject;
    /** URL to be shared */
    sourceUrl: string;
    /** Whether the button should be round */
    round?: boolean;
    /** Whether the button should show as only an icon */
    onlyIcon?: boolean;
    /** Custom button text */
    buttonText?: string;
    /** Whether to show the share icon */
    isIconVisible?: boolean;
    /** Additional class name */
    className?: string;
    /** Button size */
    size?: Size;
}

export const MarketplaceSocialShareButton: React.FC<MarketplaceSocialShareButtonProps> = ({
    buttonStyle = 'ghost',
    context,
    fullWidth = false,
    load,
    shareableObject,
    sourceUrl,
    round = false,
    onlyIcon = false,
    buttonText,
    isIconVisible = false,
    className,
    size = 'medium',
}) => {
    const [isShown, setIsShown] = useState(false);
    const {gettext} = useI18n();
    const deviceType = useDeviceType();
    const socialShare = useSocialShare({shareableObject, sourceUrl, context});

    // Store lazy component in state to ensure stable reference
    // Using useState with lazy initializer ensures component is created once
    const [LazyShareContent] = useState(() => React.lazy(() => load()));

    const getTitle = useCallback(() => {
        if (!socialShare.sharedUrl) {
            return gettext('Loading');
        }
        if (socialShare.isImportEmailModalShown) {
            return gettext('Share via email');
        }
        if (shareableObject.isFree) {
            return gettext('Share');
        }
        return gettext('Share this course');
    }, [
        socialShare.sharedUrl,
        socialShare.isImportEmailModalShown,
        shareableObject.isFree,
        gettext,
    ]);

    const openContainer = useCallback(async () => {
        if (context === SOURCE_PAGES.CLP) {
            publishEvent(
                new CourseShareEvent({
                    courseId: parseInt(shareableObject.id, 10),
                }),
            );
        }
        setIsShown(true);
        if (!socialShare.sharedUrl) {
            await socialShare.getOrPopulateUrl();
        }
    }, [context, socialShare, shareableObject.id]);

    const closeContainer = useCallback(() => {
        setIsShown(false);
        socialShare.hideImportEmailModal();
    }, [socialShare]);

    if (!isMarketplaceShareEnabled(shareableObject)) {
        return null;
    }

    const Dialog = deviceType === 'desktop' ? Modal : BottomDrawer;
    const shareText = buttonText || gettext('Share');

    return (
        <>
            {round || onlyIcon ? (
                <IconButton
                    onClick={openContainer}
                    udStyle={buttonStyle}
                    round={round}
                    size={size}
                    data-purpose="social-share-button"
                    className={className}
                    aria-label={shareText}
                >
                    <Icon
                        glyph="share"
                        data-testid="share-icon"
                        aria-hidden="true"
                        label={shareText}
                    />
                </IconButton>
            ) : (
                <Button
                    componentClass="button"
                    onClick={openContainer}
                    size={size}
                    style={fullWidth ? {width: '100%'} : {}}
                    udStyle={buttonStyle}
                    data-purpose="social-share-button"
                    className={className}
                    aria-label={shareText}
                >
                    <span>{shareText}</span>
                    {isIconVisible && (
                        <Icon
                            glyph="share"
                            data-testid="share-icon"
                            aria-hidden="true"
                            label={shareText}
                        />
                    )}
                </Button>
            )}
            <Dialog isOpen={isShown} onClose={closeContainer} title={getTitle()}>
                {socialShare.sharedUrl ? (
                    <React.Suspense fallback={<Loader block={true} size="medium" />}>
                        <LazyShareContent
                            onFormCancel={socialShare.hideImportEmailModal}
                            isImportEmailModalShown={socialShare.isImportEmailModalShown}
                            sharedUrl={socialShare.sharedUrl}
                            hideImportEmailModal={socialShare.hideImportEmailModal}
                            share={socialShare.share}
                            sourcePage={context}
                            shareableObject={shareableObject}
                        />
                    </React.Suspense>
                ) : (
                    <Loader block={true} size="medium" />
                )}
            </Dialog>
        </>
    );
};
